import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Upload, X, Loader2, Save, FileText } from "lucide-react";

function fileToDataUri(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Couldn't read file"));
        reader.readAsDataURL(file);
    });
}

const STAGES = { PICK: "pick", BUSY: "busy", REVIEW: "review", SAVING: "saving" };

/**
 * AI-extract vessel data + maintenance recommendations from a marine survey PDF.
 *
 * Stages:
 *   PICK   — upload PDF (max 20 MB)
 *   BUSY   — backend renders first 12 pages, sends to Claude Vision, parses JSON
 *   REVIEW — editable checklist of every extracted field/engine/tank/electronic/recommendation
 *   SAVING — PATCH the vessel + POST recommendation Maintenance entries
 */
export default function SurveyExtractModal({ vesselId, vessel, onClose, onApplied }) {
    const [stage, setStage] = useState(STAGES.PICK);
    const [filename, setFilename] = useState("");
    const [err, setErr] = useState("");
    const [extracted, setExtracted] = useState(null);
    // Per-field toggles (default all on for fields with non-null values)
    const [fieldOn, setFieldOn] = useState({});
    // Editable arrays
    const [engines, setEngines] = useState([]);
    const [tanks, setTanks] = useState([]);
    const [recs, setRecs] = useState([]);
    const fileRef = useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        setErr("");
        if (file.size > 20 * 1024 * 1024) {
            setErr(`Survey too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Use a PDF under 20 MB.`);
            return;
        }
        try {
            setFilename(file.name);
            const duri = await fileToDataUri(file);
            setStage(STAGES.BUSY);
            const { data } = await api.post("/surveys/extract", { data_uri: duri });
            if (!data.ok) {
                setErr(data.note || "Couldn't read this survey.");
                setStage(STAGES.PICK);
                return;
            }
            setExtracted(data);
            // Seed toggle state — only fields with non-null values are turned on
            const fieldKeys = Object.keys(data.vessel || {});
            const tmp = {};
            for (const k of fieldKeys) {
                tmp[`vessel.${k}`] = data.vessel[k] != null && data.vessel[k] !== "";
            }
            setFieldOn(tmp);
            setEngines((data.engines || []).map((e, i) => ({ ...e, _key: `eng-${i}`, _on: true })));
            setTanks((data.tanks || []).map((t, i) => ({ ...t, _key: `tnk-${i}`, _on: true })));
            setRecs((data.recommendations || []).map((r, i) => ({ ...r, _key: `rec-${i}`, _on: r.priority !== "informational" })));
            setStage(STAGES.REVIEW);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't read this survey.");
            setStage(STAGES.PICK);
        }
    };

    const apply = async () => {
        setErr("");
        setStage(STAGES.SAVING);
        try {
            // 1. Build vessel PATCH payload. Map survey fields → existing vessel fields:
            //    - name → vessel.name (only if user enabled it AND vessel.name is blank/different)
            //    - everything else lives in vessel.specs.* using sensible keys
            const ev = extracted.vessel || {};
            const specs = { ...(vessel.specs || {}) };
            const general = { ...(specs.general || {}) };
            const enabledKey = (k) => fieldOn[`vessel.${k}`];

            const patch = {};
            if (enabledKey("name") && ev.name) patch.name = ev.name;

            const generalKeys = [
                "year", "builder", "model", "hin", "length_ft", "beam_ft", "draft_ft",
                "hull_material", "displacement_lbs", "documented_number",
                "registration_number", "fuel_type",
            ];
            for (const k of generalKeys) {
                if (enabledKey(k) && ev[k] != null && ev[k] !== "") {
                    general[k] = ev[k];
                }
            }
            if (Object.keys(general).length > 0) {
                specs.general = general;
                patch.specs = specs;
            }

            // 2. Engines — replace if the user enabled any. Preserve existing engines'
            //    ids if their labels match (so per-engine maintenance tags stay linked).
            const engOn = engines.filter((e) => e._on && (e.label || "").trim());
            if (engOn.length > 0) {
                const existingByLabel = new Map(
                    (vessel.engines || []).map((e) => [(e.label || "").toLowerCase(), e])
                );
                patch.engines = engOn.map((e) => {
                    const existing = existingByLabel.get((e.label || "").toLowerCase());
                    return {
                        id: existing?.id,
                        label: e.label,
                        make: e.make || null,
                        model: e.model || null,
                        serial: e.serial || null,
                        year: e.year || null,
                        hp_kw: e.hp_kw || null,
                        hours_current: parseFloat(e.hours_current) || (existing?.hours_current ?? 0),
                        fuel_type: e.fuel_type || null,
                    };
                });
            }

            // 3. Tanks — same merge-by-label strategy
            const tnkOn = tanks.filter((t) => t._on && (t.label || "").trim());
            if (tnkOn.length > 0) {
                const existingTanksByLabel = new Map(
                    (vessel.tanks || []).map((t) => [(t.label || "").toLowerCase(), t])
                );
                patch.tanks = tnkOn.map((t) => {
                    const existing = existingTanksByLabel.get((t.label || "").toLowerCase());
                    return {
                        id: existing?.id,
                        label: t.label,
                        capacity: t.capacity == null ? null : parseFloat(t.capacity),
                        capacity_unit: t.capacity_unit || "gal_us",
                        fuel_type: t.fuel_type || null,
                    };
                });
            }

            if (Object.keys(patch).length > 0) {
                await api.patch(`/vessels/${vesselId}`, patch);
            }

            // 4. Recommendations → bulk-POST as Maintenance entries with status=scheduled
            const recsOn = recs.filter((r) => r._on && (r.title || "").trim());
            for (const r of recsOn) {
                await api.post(`/vessels/${vesselId}/entries`, {
                    category: r.category || "Maintenance",
                    date: null,
                    title: r.title.trim(),
                    status: r.priority === "urgent" ? "overdue" : "scheduled",
                    notes: [r.notes, extracted.surveyor ? `Source: ${extracted.surveyor}` : ""].filter(Boolean).join(" — "),
                    custom_data: {
                        from_survey: extracted.surveyor || true,
                        survey_date: extracted.survey_date || null,
                        priority: r.priority || "recommended",
                    },
                });
            }

            onApplied?.({ recsAdded: recsOn.length });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't save changes.");
            setStage(STAGES.REVIEW);
        }
    };

    const toggleVesselField = (k) => setFieldOn((s) => ({ ...s, [`vessel.${k}`]: !s[`vessel.${k}`] }));

    return (
        <div data-testid="survey-extract-modal" className="bm-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "grid", placeItems: "center", padding: 20, zIndex: 100 }} onClick={stage === STAGES.SAVING ? undefined : onClose}>
            <div className="bm-card" style={{ padding: 28, maxWidth: 860, width: "100%", maxHeight: "92vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <h3 className="bm-serif" style={{ fontSize: 24, margin: 0, fontWeight: 500 }}>
                        <Sparkles size={18} color="var(--bm-gold)" style={{ verticalAlign: -3, marginRight: 6 }} />
                        Extract from survey PDF
                    </h3>
                    <button onClick={onClose} className="bm-btn bm-btn-ghost" style={{ padding: 6 }} aria-label="Close" disabled={stage === STAGES.SAVING}>
                        <X size={16} />
                    </button>
                </div>

                {stage === STAGES.PICK && (
                    <div data-testid="survey-stage-pick">
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6 }}>
                            Upload a marine survey PDF (under 20 MB). AI will scan the first 12 pages and pull out vessel specs, engines, tanks, electronics, and maintenance recommendations. You'll review every item before anything is written to your boat.
                        </p>
                        <input data-testid="survey-file" ref={fileRef} type="file" accept="application/pdf" onChange={(e) => handleFile(e.target.files?.[0])} style={{ display: "none" }} />
                        <button data-testid="survey-upload-btn" className="bm-btn bm-btn-gold" onClick={() => fileRef.current?.click()} style={{ marginTop: 18, padding: "14px 24px", fontSize: 15 }}>
                            <Upload size={16} /> Choose a survey PDF
                        </button>
                        {err && <div data-testid="survey-err" style={{ color: "var(--bm-bad)", marginTop: 14, fontSize: 13 }}>{err}</div>}
                    </div>
                )}

                {stage === STAGES.BUSY && (
                    <div data-testid="survey-stage-busy" style={{ padding: "40px 20px", textAlign: "center", color: "var(--bm-text-muted)" }}>
                        <Loader2 size={28} color="var(--bm-gold)" style={{ animation: "spin 1.4s linear infinite", marginBottom: 14 }} />
                        <div style={{ fontSize: 14 }}>Reading {filename}…</div>
                        <div style={{ fontSize: 12, marginTop: 4, color: "var(--bm-text-dim)" }}>Surveys are long. This usually takes 60-90 seconds.</div>
                        <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
                    </div>
                )}

                {stage === STAGES.REVIEW && extracted && (
                    <div data-testid="survey-stage-review" style={{ fontSize: 13 }}>
                        <div style={{ marginBottom: 12, color: "var(--bm-text-muted)" }}>
                            <FileText size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                            {extracted.surveyor ? <>By <strong style={{ color: "var(--bm-text)" }}>{extracted.surveyor}</strong></> : "Unknown surveyor"}
                            {extracted.survey_date ? <> · Dated {extracted.survey_date}</> : ""}
                        </div>

                        {/* Vessel fields */}
                        <Section title={`Vessel Info (${Object.keys(fieldOn).filter((k) => fieldOn[k]).length} of ${Object.keys(extracted.vessel || {}).length})`}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
                                {Object.entries(extracted.vessel || {}).map(([k, v]) => {
                                    if (v == null || v === "") return null;
                                    return (
                                        <label key={k} data-testid={`survey-vfield-${k}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid var(--bm-border-soft)", borderRadius: "var(--bm-r-md)", background: fieldOn[`vessel.${k}`] ? "rgba(212,181,114,0.06)" : "transparent" }}>
                                            <input type="checkbox" checked={!!fieldOn[`vessel.${k}`]} onChange={() => toggleVesselField(k)} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 11, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.replace(/_/g, " ")}</div>
                                                <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>{String(v)}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </Section>

                        {/* Engines */}
                        {engines.length > 0 && (
                            <Section title={`Engines (${engines.filter((e) => e._on).length} of ${engines.length})`}>
                                <ArrayList
                                    items={engines}
                                    setItems={setEngines}
                                    fields={[
                                        { key: "label", label: "Label", testid: "label" },
                                        { key: "make", label: "Make", testid: "make" },
                                        { key: "model", label: "Model", testid: "model" },
                                        { key: "hours_current", label: "Hours", testid: "hours", type: "number" },
                                    ]}
                                    testidPrefix="survey-engine"
                                />
                            </Section>
                        )}

                        {/* Tanks */}
                        {tanks.length > 0 && (
                            <Section title={`Fuel Tanks (${tanks.filter((t) => t._on).length} of ${tanks.length})`}>
                                <ArrayList
                                    items={tanks}
                                    setItems={setTanks}
                                    fields={[
                                        { key: "label", label: "Label", testid: "label" },
                                        { key: "capacity", label: "Capacity", testid: "capacity", type: "number" },
                                        { key: "capacity_unit", label: "Unit", testid: "unit" },
                                        { key: "fuel_type", label: "Fuel", testid: "fuel" },
                                    ]}
                                    testidPrefix="survey-tank"
                                />
                            </Section>
                        )}

                        {/* Recommendations → Maintenance entries */}
                        {recs.length > 0 && (
                            <Section title={`Recommendations to add as Maintenance (${recs.filter((r) => r._on).length} of ${recs.length})`}>
                                <div style={{ display: "grid", gap: 8 }}>
                                    {recs.map((r, i) => (
                                        <label key={r._key} data-testid={`survey-rec-${i}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", border: "1px solid var(--bm-border-soft)", borderRadius: "var(--bm-r-md)", background: r._on ? "rgba(212,181,114,0.06)" : "transparent" }}>
                                            <input type="checkbox" checked={r._on} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, _on: e.target.checked } : x))} style={{ marginTop: 4 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                                                    {r.priority && (
                                                        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", padding: "2px 8px", borderRadius: 999, background: r.priority === "urgent" ? "rgba(229,87,87,0.18)" : r.priority === "recommended" ? "rgba(212,181,114,0.15)" : "rgba(91,185,232,0.12)", color: r.priority === "urgent" ? "var(--bm-bad)" : r.priority === "recommended" ? "var(--bm-gold)" : "var(--bm-blue)" }}>
                                                            {r.priority}
                                                        </span>
                                                    )}
                                                    <span style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>→ {r.category}</span>
                                                </div>
                                                <input className="bm-input" style={{ padding: "4px 8px", fontSize: 13, marginBottom: r.notes ? 6 : 0 }} value={r.title} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
                                                {r.notes && <div style={{ fontSize: 12, color: "var(--bm-text-muted)", lineHeight: 1.4 }}>{r.notes}</div>}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {err && <div data-testid="survey-err" style={{ color: "var(--bm-bad)", marginTop: 10, fontSize: 13 }}>{err}</div>}

                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
                            <button data-testid="survey-cancel-btn" type="button" className="bm-btn bm-btn-ghost" onClick={onClose}>Cancel</button>
                            <button data-testid="survey-apply-btn" type="button" className="bm-btn bm-btn-gold" onClick={apply}>
                                <Save size={14} /> Apply selected
                            </button>
                        </div>
                    </div>
                )}

                {stage === STAGES.SAVING && (
                    <div data-testid="survey-stage-saving" style={{ padding: "40px 20px", textAlign: "center", color: "var(--bm-text-muted)" }}>
                        <Loader2 size={28} color="var(--bm-gold)" style={{ animation: "spin 1.4s linear infinite", marginBottom: 14 }} />
                        <div style={{ fontSize: 14 }}>Applying changes to your vessel…</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 18 }}>
            <h4 className="bm-serif" style={{ fontSize: 15, color: "var(--bm-gold)", margin: "0 0 10px", fontWeight: 600, letterSpacing: "0.02em" }}>{title}</h4>
            {children}
        </div>
    );
}

function ArrayList({ items, setItems, fields, testidPrefix }) {
    return (
        <div style={{ display: "grid", gap: 8 }}>
            {items.map((it, i) => (
                <label key={it._key} data-testid={`${testidPrefix}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--bm-border-soft)", borderRadius: "var(--bm-r-md)", background: it._on ? "rgba(212,181,114,0.06)" : "transparent" }}>
                    <input type="checkbox" checked={it._on} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, _on: e.target.checked } : x))} />
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${fields.length}, 1fr)`, gap: 8, flex: 1, minWidth: 0 }}>
                        {fields.map((f) => (
                            <div key={f.key}>
                                <div style={{ fontSize: 10, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{f.label}</div>
                                <input
                                    data-testid={`${testidPrefix}-${i}-${f.testid}`}
                                    className="bm-input"
                                    type={f.type || "text"}
                                    style={{ padding: "4px 8px", fontSize: 12 }}
                                    value={it[f.key] ?? ""}
                                    onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, [f.key]: e.target.value } : x))}
                                />
                            </div>
                        ))}
                    </div>
                </label>
            ))}
        </div>
    );
}
